import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, User, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { cn } from '../lib/utils';
import { AffiliateWithStats } from '../types';
import { searchAffiliates } from '../services/affiliateService';

interface AffiliateSelectProps {
  value?: string;
  onChange: (affiliateId: string | undefined) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function AffiliateSelect({ 
  value, 
  onChange, 
  placeholder = "Select affiliate (optional)", 
  className = "",
  disabled = false 
}: AffiliateSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [affiliates, setAffiliates] = useState<AffiliateWithStats[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load approved affiliates when component mounts or dropdown opens
  useEffect(() => {
    const loadAffiliates = async () => {
      if (affiliates.length === 0) {
        try {
          setLoading(true);
          const result = await searchAffiliates('', 'approved');
          setAffiliates(result.affiliates);
        } catch (error) {
          console.error('Error loading affiliates:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    if (isOpen) {
      loadAffiliates();
    }
  }, [isOpen, affiliates.length]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelectAffiliate = (affiliateId: string | undefined) => {
    onChange(affiliateId);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Create filter options including "None" and existing affiliates
  const allOptions = [
    { id: undefined, userName: 'None (no affiliate)', userEmail: '' },
    ...affiliates
  ];
  
  const filteredAffiliates = allOptions.filter(affiliate =>
    affiliate.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    affiliate.userEmail.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getDisplayValue = () => {
    if (!value) return placeholder;
    const selectedAffiliate = affiliates.find(a => a.id === value);
    return selectedAffiliate ? `${selectedAffiliate.userName} (${selectedAffiliate.userEmail})` : placeholder;
  };

  const selectedAffiliate = affiliates.find(a => a.id === value);

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      <Button
        type="button"
        variant="outline"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          "w-full justify-between text-left font-normal",
          !value && "text-muted-foreground",
          isOpen && "ring-2 ring-jamaica-green ring-opacity-20"
        )}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
          <span className="truncate">
            {selectedAffiliate ? (
              <span>
                <span className="font-medium">{selectedAffiliate.userName}</span>
                <span className="text-gray-500 ml-1">({selectedAffiliate.userEmail})</span>
              </span>
            ) : (
              placeholder
            )}
          </span>
        </div>
        <ChevronDown className={cn(
          "h-4 w-4 transition-transform duration-200 flex-shrink-0",
          isOpen && "transform rotate-180"
        )} />
      </Button>

      {isOpen && (
        <div className="absolute top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                ref={inputRef}
                placeholder="Search affiliates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-8 text-sm"
              />
            </div>
          </div>

          <div className="max-h-48 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                <span className="ml-2 text-sm text-gray-500">Loading affiliates...</span>
              </div>
            ) : filteredAffiliates.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                No affiliates found
              </div>
            ) : (
              filteredAffiliates.map((affiliate) => (
                <button
                  key={affiliate.id || 'none'}
                  onClick={() => handleSelectAffiliate(affiliate.id)}
                  className={cn(
                    "w-full px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors",
                    value === affiliate.id && "bg-jamaica-green/5 text-jamaica-green"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-900 truncate">
                        {affiliate.userName}
                      </div>
                      {affiliate.userEmail && (
                        <div className="text-xs text-gray-500 truncate">
                          {affiliate.userEmail}
                        </div>
                      )}
                    </div>
                    {value === affiliate.id && (
                      <div className="w-2 h-2 bg-jamaica-green rounded-full flex-shrink-0" />
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
} 